import { Component, DestroyRef, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { EnfafCountrySelectorComponent } from '../../../../../components/enfaf-country-selector/enfaf-country-selector.component';
import { EnfafInputComponent } from '../../../../../components/enfaf-input/enfaf-input.component';
import { EnfafModalComponent } from '../../../../../components/enfaf-modal/enfaf-modal.component';
import { Country } from '../../../../../models/interfaces/country.interface';

/**
 * Optional data for the modal. The students list opens it without any; Lendiplan asks for the DNI
 * (Lendismart needs it) and only offers the countries its merchants serve.
 */
export interface ModalAddStudentData {
  requireDni?: boolean;
  /** ISO codes of the countries to offer; all countries when absent. */
  allowedCountryCodes?: string[];
}

@Component({
  selector: 'app-modal-add-student',
  imports: [FormsModule, ReactiveFormsModule, EnfafModalComponent, EnfafInputComponent, EnfafCountrySelectorComponent],
  templateUrl: './modal-add-student.component.html',
  styleUrl: './modal-add-student.component.scss'
})
export class ModalAddStudentComponent implements OnInit, OnDestroy {
  studentForm: FormGroup = new FormGroup({});
  showCloseAlert = false;
  isSavingStudent = false;
  countries: Country[] = [];
  private syncingCountryPrefix = false;

  private unsubscribe$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ModalAddStudentData | null,
    private dialogRef: MatDialogRef<ModalAddStudentComponent>,
    private alert: AlertService,
    private api: ApiService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.fetchCountries();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  initForm(): void {
    this.studentForm = new FormGroup({
      email: new FormControl(null, [Validators.required, Validators.email]),
      name: new FormControl(null, Validators.required),
      lastname: new FormControl(null, Validators.required),
      countryId: new FormControl(null, Validators.required),
      phone: new FormControl(null, Validators.required),
      phonePrefix: new FormControl(null, Validators.required),
      // Required for Lendiplan: a DNI of only spaces would be trimmed away on save, so it must contain some character.
      dni: new FormControl(
        null,
        this.data?.requireDni ? [Validators.required, Validators.pattern(/\S/), Validators.maxLength(50)] : Validators.maxLength(50)
      )
    });
    this.setupCountryPrefixSync();
    this.listenForChanges();
  }

  private async fetchCountries(): Promise<void> {
    try {
      const response = await firstValueFrom(this.api.fetchCountries());
      const allowed = this.data?.allowedCountryCodes;
      this.countries = allowed ? response.countries.filter((country: Country) => allowed.includes(country.countryCode)) : response.countries;
      this.studentForm.markAsPristine();
      this.showCloseAlert = false;
    } catch (errorResponse: any) {
      this.toast.presentErrorToast(errorResponse?.error?.message || 'Hubo un error buscando países.');
    }
  }

  private setupCountryPrefixSync(): void {
    this.studentForm.controls.countryId.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((country: Country | null) => {
      if (this.syncingCountryPrefix) return;
      this.syncingCountryPrefix = true;
      this.studentForm.controls.phonePrefix.setValue(country?.phoneCode || null, { emitEvent: false });
      this.syncingCountryPrefix = false;
    });
  }

  listenForChanges(): void {
    this.studentForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      this.showCloseAlert = true;
    });
  }

  async confirmCloseModal(): Promise<any> {
    if (this.showCloseAlert) {
      const closeModal = await this.alert.presentCloseWindowAlert();
      return closeModal ? this.closeModal() : null;
    }

    this.closeModal();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  async saveStudent(): Promise<void> {
    try {
      this.isSavingStudent = true;
      const body = {
        ...this.studentForm.value
      };
      body.countryId = body.countryId?.countryId;
      body.email = body.email.trim().toLowerCase();
      body.dni = body.dni?.trim() || undefined;
      const response = await firstValueFrom(this.api.saveNewStudent(body));
      this.isSavingStudent = false;
      // The API does not echo the country back; callers like Lendiplan need it to pick a merchant.
      this.closeModal({ newStudent: { ...response.newStudent, countryId: body.countryId } });
      this.toast.presentSuccessToast(response.message);
    } catch (errorResponse: any) {
      this.isSavingStudent = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error guardando estudiante');
    }
  }
}
